import { z } from "zod";
import { CASE_LIST_VIEWS, CASE_STATUSES, PART_EXCEPTIONS, ROLES, WHOLESALE_LIST_VIEWS } from "./domain.js";
import type { CaseStatus, EmailEventKind, EmailState, PartException, Role } from "./domain.js";
import { normalizeName, normalizePartNumber, normalizeVin } from "./formatting.js";

/**
 * API contracts shared by the Express routes and the React client.
 *
 * Zod schemas validate every request body on the server (`validateBody`) and
 * drive client-side field errors; the DTO types below are the canonical public
 * shapes from the implementation plan. Business-critical fields — `status`,
 * `arrivedAt`, totals, audit actors, email recipients — are never accepted
 * from the browser, so no schema here declares them.
 */

// ---------------------------------------------------------------------------
// Validation limits (plan: "Validation limits")
// ---------------------------------------------------------------------------

export const LIMITS = {
  name: { min: 1, max: 120 },
  email: { min: 3, max: 254 },
  password: { min: 12, max: 128 },
  ro: { min: 1, max: 50 },
  vehicleText: { min: 1, max: 80 },
  partNumber: { min: 1, max: 80 },
  description: { min: 1, max: 200 },
  quantity: { min: 1, max: 999 },
  note: { min: 1, max: 2000 },
  reason: { min: 5, max: 500 },
  vendorReference: { min: 0, max: 120 },
  search: { min: 1, max: 120 },
  /** A wholesale customer's reference (PO) and the Reynolds & Reynolds number. */
  wholesaleReference: { min: 1, max: 50 },
  /** A wholesale customer's phone, as typed. */
  phone: { min: 1, max: 40 },
  /** Vehicle model year: 1980 through the current Chicago calendar year + 1. */
  vehicleYear: { min: 1980 },
} as const;

/**
 * How long a temporary password (account creation, administrator reset) can
 * be used to sign in, counted from the instant it was issued. It still has to
 * be replaced at that first sign-in; once it is, nothing expires.
 */
export const TEMPORARY_PASSWORD_TTL_HOURS = 72;

/** Cursor pages (case lists, search hits, activity): default and maximum rows per page. */
export const PAGE_SIZE = { default: 40, max: 100 } as const;

/** The dealership's timezone until a later configuration explicitly changes it. */
export const DEALERSHIP_TIMEZONE = "America/Chicago";

/**
 * The dealership as a printed page names it (the stock order report's
 * header): the group the sign-in logo already reads. The product keeps its
 * own name ("Chicagoland Auto Tools") — this is whose parts are on the sheet.
 */
export const DEALERSHIP_NAME = "Chicagoland Auto Group";

export const MIN_VEHICLE_YEAR = LIMITS.vehicleYear.min;

const chicagoYearFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: DEALERSHIP_TIMEZONE,
  year: "numeric",
});

/**
 * The highest vehicle year accepted at `now`: the calendar year in
 * `America/Chicago` plus one (next year's models are on the lot before
 * January). Computed from the injected clock so the boundary is testable and
 * a late-December evening in Chicago is not already "next year" just because
 * UTC has turned.
 */
export function maxVehicleYearAt(now: Date): number {
  return Number(chicagoYearFormatter.format(now)) + 1;
}

// ---------------------------------------------------------------------------
// Primitive schemas
// ---------------------------------------------------------------------------

export const roleSchema = z.enum(ROLES);
export const caseStatusSchema = z.enum(CASE_STATUSES);
export const partExceptionSchema = z.enum(PART_EXCEPTIONS);
export const caseListViewSchema = z.enum(CASE_LIST_VIEWS);

export const uuidSchema = z.uuid();

/** Display names: trimmed, internal whitespace collapsed, 1–120 characters. */
export const displayNameSchema = z
  .string()
  .transform(normalizeName)
  .pipe(z.string().min(LIMITS.name.min).max(LIMITS.name.max));

/** An address that can receive mail: trimmed, RFC-shaped, 3–254 characters. */
export const emailSchema = z
  .string()
  .trim()
  .pipe(z.email().min(LIMITS.email.min).max(LIMITS.email.max));

/** A password chosen by a person: 12–128 characters, never trimmed. */
export const passwordSchema = z.string().min(LIMITS.password.min).max(LIMITS.password.max);

/** The length messages a person can act on; the change-password page shows the same words. */
export const PASSWORD_TOO_SHORT_MESSAGE = `Use at least ${LIMITS.password.min} characters.`;
export const PASSWORD_TOO_LONG_MESSAGE = `Use at most ${LIMITS.password.max} characters.`;
/** A chosen temporary password with a space at either end is refused, never silently trimmed. */
export const PASSWORD_PADDED_MESSAGE = "A password can't begin or end with a space.";
/** …and so is one whose inner whitespace is anything but single spaces ({@link hasOnlySingleSpaces}). */
export const PASSWORD_SPACING_MESSAGE = "Separate words with a single space.";

/**
 * Whether every run of whitespace inside the password is exactly one
 * ordinary space. An HTML mail client folds two spaces, or a tab, into one,
 * and a non-breaking space looks like a space but is not typed as one — so
 * the recipient would type what they read and be refused. Refusing such a
 * value up front, with a message the administrator can act on, is the only
 * fix that holds in every mail client (Outlook's renderer ignores
 * `white-space`).
 */
export function hasOnlySingleSpaces(password: string): boolean {
  return !/\s\s|[^\S ]/.test(password);
}

/**
 * A temporary password the administrator types instead of having one
 * generated (owner decision 2026-09-17: the dealership's mail server purges
 * the invitation emails, so the administrator hands the password over
 * themselves). The same 12–128 characters a chosen password must be, and —
 * like every password — never trimmed: a space or line break at either end
 * is refused with a field error rather than dropped, because the person
 * will type exactly what they were handed. Inner whitespace has to be
 * single spaces for the same reason ({@link hasOnlySingleSpaces}). One
 * whitespace message per refusal: a value that is padded is reported as
 * padded, whatever its inside holds.
 */
export const temporaryPasswordSchema = z
  .string()
  .min(LIMITS.password.min, PASSWORD_TOO_SHORT_MESSAGE)
  .max(LIMITS.password.max, PASSWORD_TOO_LONG_MESSAGE)
  .superRefine((password, context) => {
    const message =
      password !== password.trim()
        ? PASSWORD_PADDED_MESSAGE
        : hasOnlySingleSpaces(password)
          ? undefined
          : PASSWORD_SPACING_MESSAGE;
    if (message !== undefined) context.addIssue({ code: "custom", message });
  });

/** Optimistic-concurrency version as the client last saw it. */
export const expectedVersionSchema = z.number().int().nonnegative();

/** A correction, override, cancellation or exception reason: trimmed, 5–500 characters. */
export const reasonSchema = z.string().trim().min(LIMITS.reason.min).max(LIMITS.reason.max);

/**
 * A blank or whitespace-only string means "not provided" for optional text
 * fields, so a form can always send every field. Non-strings fall through to
 * the inner schema and fail with its message.
 */
function blankToUndefined(value: unknown): unknown {
  return typeof value === "string" && value.trim() === "" ? undefined : value;
}

/** A creation form may send `null` for a field it has no value for; that is "not provided" too. */
function nullOrBlankToUndefined(value: unknown): unknown {
  return value === null ? undefined : blankToUndefined(value);
}

/**
 * The same rule for a header patch, where `null` is the clearing value: a
 * blank string clears the field rather than failing its format check, and
 * an absent field stays absent (untouched).
 */
function blankToNull(value: unknown): unknown {
  return typeof value === "string" && value.trim() === "" ? null : value;
}

// ---------------------------------------------------------------------------
// Parts cases: identifiers and part lines
// ---------------------------------------------------------------------------

const VIN_SHAPE = /^(?:[A-Z0-9]{8}|[A-Z0-9]{17})$/;

/**
 * A full VIN or exactly the last eight VIN characters. Spaces and hyphens are
 * removed and the value upper-cased before the length check, so the stored
 * form is the normalized form (`normalizeVin`). The VIN itself is optional on
 * an order (owner decision 2026-09-17; spec §5 had required it): the commands
 * below wrap this schema so a blank or absent value means "no VIN", while a
 * value that is given still has to be one of the two shapes.
 */
export const vinSchema = z
  .string()
  .transform(normalizeVin)
  .pipe(
    z
      .string()
      .regex(VIN_SHAPE, "Enter the full 17-character VIN or exactly its last eight characters."),
  );

/**
 * An RO number as Parts typed it, trimmed with internal whitespace collapsed
 * (display form). The duplicate key (`normalizeRo`) is derived by the server.
 */
export const roNumberSchema = z
  .string()
  .transform(normalizeName)
  .pipe(z.string().min(LIMITS.ro.min).max(LIMITS.ro.max));

/** Vehicle make or model: trimmed, whitespace collapsed, 1–80 characters. */
export const vehicleTextSchema = z
  .string()
  .transform(normalizeName)
  .pipe(z.string().min(LIMITS.vehicleText.min).max(LIMITS.vehicleText.max));

/** `vehicleYear` for requests validated at `now`. */
export function vehicleYearSchemaAt(now: Date) {
  const max = maxVehicleYearAt(now);
  return z
    .number()
    .int()
    .min(MIN_VEHICLE_YEAR, `Enter a year from ${MIN_VEHICLE_YEAR} through ${max}.`)
    .max(max, `Enter a year from ${MIN_VEHICLE_YEAR} through ${max}.`);
}

/** Optional appointment date, ISO calendar date. */
export const appointmentDateSchema = z.iso.date();

/** Optional vendor/order reference, 0–120 characters; blank means none. */
export const vendorOrderReferenceSchema = z.preprocess(
  blankToUndefined,
  z.string().trim().max(LIMITS.vendorReference.max).optional(),
);

/** Optional order note, 1–2000 characters; blank means none. */
export const orderNoteSchema = z.preprocess(
  blankToUndefined,
  z.string().trim().min(LIMITS.note.min).max(LIMITS.note.max).optional(),
);

/**
 * A part number as Parts typed it (display form), 1–80 characters, that
 * still contains at least one letter or digit once punctuation is removed —
 * the duplicate and search key (`normalizePartNumber`) must not be empty.
 */
export const partNumberSchema = z
  .string()
  .trim()
  .min(LIMITS.partNumber.min)
  .max(LIMITS.partNumber.max)
  .refine((value) => normalizePartNumber(value).length > 0, {
    message: "A part number needs at least one letter or digit.",
  });

export const partDescriptionSchema = z
  .string()
  .trim()
  .min(LIMITS.description.min)
  .max(LIMITS.description.max);

export const quantitySchema = z.number().int().min(LIMITS.quantity.min).max(LIMITS.quantity.max);

/** Exactly one ETA choice: a calendar date, or an explicit "No ETA". */
export const etaInputSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("date"), date: z.iso.date() }),
  z.strictObject({ kind: z.literal("no_eta") }),
]);

export const partLineInputSchema = z.object({
  partNumber: partNumberSchema,
  description: partDescriptionSchema,
  quantityOrdered: quantitySchema,
  eta: etaInputSchema,
});

/**
 * A line as the order forms send it (spec §6.5): `eta` may be left out when
 * the order carries a master ETA, which then fills it. The service — not the
 * schema — refuses a blank line on an order without one, naming the line.
 */
export const createPartLineInputSchema = partLineInputSchema.extend({
  eta: etaInputSchema.optional(),
});

/** "ETA for all parts": an ISO calendar date; blank means none. */
export const masterEtaSchema = z.iso.date();

/** The version the client last saw plus an optional correction reason. */
export const mutationMetaSchema = z.object({
  expectedVersion: expectedVersionSchema,
  correctionReason: reasonSchema.optional(),
});

// ---------------------------------------------------------------------------
// Parts cases: commands
// ---------------------------------------------------------------------------

/**
 * `POST /api/cases`, validated at `now` (the vehicle-year ceiling depends on
 * it). The VIN, the technician and the RO are the three optional
 * identifiers: blank or absent means none (owner decision 2026-09-17 for the
 * first two; the RO was always optional, spec §5).
 */
export function createCaseInputSchemaAt(now: Date) {
  return z.object({
    vin: z.preprocess(blankToUndefined, vinSchema.optional()),
    customerName: displayNameSchema,
    vehicleYear: vehicleYearSchemaAt(now),
    vehicleMake: vehicleTextSchema,
    vehicleModel: vehicleTextSchema,
    advisorId: uuidSchema,
    technicianId: z.preprocess(blankToUndefined, uuidSchema.optional()),
    roNumber: z.preprocess(blankToUndefined, roNumberSchema.optional()),
    appointmentDate: z.preprocess(blankToUndefined, appointmentDateSchema.optional()),
    vendorOrderReference: vendorOrderReferenceSchema,
    orderNote: orderNoteSchema,
    masterEta: z.preprocess(blankToUndefined, masterEtaSchema.optional()),
    /** The customer's car is at the dealership now; absent means it is not. */
    carInShop: z.boolean().optional(),
    duplicateOverrideReason: z.preprocess(blankToUndefined, reasonSchema.optional()),
    partLines: z.array(createPartLineInputSchema).min(1, "Add at least one part line."),
  });
}

/**
 * `PATCH /api/cases/:caseId`. Every header field is optional; the nullable
 * ones (`vin`, `technicianId`, `roNumber`, `appointmentDate`,
 * `vendorOrderReference`, `orderNote`, `masterEta`) accept `null` to clear — a blank VIN
 * or technician clears too, since that is what an emptied field sends. At
 * least one field must be present.
 */
export function updateCaseHeaderInputSchemaAt(now: Date) {
  return z.object({
    patch: z
      .object({
        vin: z.preprocess(blankToNull, vinSchema.nullable().optional()),
        customerName: displayNameSchema.optional(),
        vehicleYear: vehicleYearSchemaAt(now).optional(),
        vehicleMake: vehicleTextSchema.optional(),
        vehicleModel: vehicleTextSchema.optional(),
        advisorId: uuidSchema.optional(),
        technicianId: z.preprocess(blankToNull, uuidSchema.nullable().optional()),
        roNumber: roNumberSchema.nullable().optional(),
        appointmentDate: appointmentDateSchema.nullable().optional(),
        vendorOrderReference: z
          .string()
          .trim()
          .max(LIMITS.vendorReference.max)
          .nullable()
          .optional(),
        orderNote: z.string().trim().min(LIMITS.note.min).max(LIMITS.note.max).nullable().optional(),
        masterEta: z.preprocess(blankToNull, masterEtaSchema.nullable().optional()),
        carInShop: z.boolean().optional(),
      })
      .refine((patch) => Object.values(patch).some((value) => value !== undefined), {
        message: "Change at least one field.",
      }),
    meta: mutationMetaSchema,
    duplicateOverrideReason: z.preprocess(blankToUndefined, reasonSchema.optional()),
  });
}

/** `POST /api/cases/:caseId/cancel`. */
export const cancelCaseInputSchema = z.object({
  reason: reasonSchema,
  expectedVersion: expectedVersionSchema,
});
export type CancelCaseInput = z.infer<typeof cancelCaseInputSchema>;

/** `POST /api/cases/:caseId/lines`; a line without `eta` takes the order's master ETA. */
export const addLineInputSchema = createPartLineInputSchema.extend({
  meta: mutationMetaSchema,
});

/** `PATCH /api/cases/:caseId/lines/:lineId`. */
export const updateLineInputSchema = z.object({
  patch: partLineInputSchema
    .partial()
    .refine((patch) => Object.values(patch).some((value) => value !== undefined), {
      message: "Change at least one field.",
    }),
  meta: mutationMetaSchema,
});

/** `POST /api/cases/:caseId/lines/:lineId/exception`; `exception: null` clears, always with a reason. */
export const setLineExceptionInputSchema = z.object({
  exception: partExceptionSchema.nullable(),
  reason: reasonSchema,
  meta: mutationMetaSchema,
});
export type SetLineExceptionInput = z.infer<typeof setLineExceptionInputSchema>;

export const caseIdParamsSchema = z.object({ caseId: uuidSchema });
export const caseLineParamsSchema = z.object({ caseId: uuidSchema, lineId: uuidSchema });
/** `/api/email-events/:emailEventId` (Parts only; spec §9 failure visibility and retry). */
export const emailEventIdParamsSchema = z.object({ emailEventId: uuidSchema });

// ---------------------------------------------------------------------------
// Parts cases: receipt verification (spec §7)
// ---------------------------------------------------------------------------

/**
 * One line of a receipt: the quantity received *now*, a delta the server adds
 * to the line's verified total. The resulting total may never exceed the
 * ordered quantity (checked by the service against the stored line).
 */
export const receiptLineInputSchema = z.object({
  partLineId: uuidSchema,
  quantityReceivedNow: z
    .number()
    .int()
    .min(1, "Enter how many were received, at least 1.")
    .max(LIMITS.quantity.max),
});
export type ReceiptLineInput = Readonly<{
  partLineId: string;
  /** A delta added to the line's verified total — never the new total. */
  quantityReceivedNow: number;
}>;

/** `POST /api/cases/:caseId/receipts/preview` — projects the outcome, writes nothing. */
export const previewReceiptInputSchema = z.object({
  receipts: z
    .array(receiptLineInputSchema)
    .min(1, "Enter a received quantity for at least one line."),
  expectedVersion: expectedVersionSchema,
});
export type PreviewReceiptInput = Readonly<{
  receipts: readonly ReceiptLineInput[];
  expectedVersion: number;
}>;

/** What the preview answers: the derived status after saving and who the email cannot reach. */
export type PreviewReceiptResult = Readonly<{
  projectedStatus: CaseStatus;
  willArrive: boolean;
  recipientWarnings: readonly RecipientWarning[];
}>;

/**
 * `POST /api/cases/:caseId/receipts`. `confirmed` is the physical-verification
 * confirmation (spec §7 step 4) and must be literally `true`.
 */
export const verifyReceiptInputSchema = previewReceiptInputSchema.extend({
  confirmed: z.literal(true, {
    error: "Confirm that the part numbers and quantities were physically verified.",
  }),
});
export type VerifyReceiptInput = Readonly<{
  receipts: readonly ReceiptLineInput[];
  /** The physical-verification confirmation; the route refuses anything else. */
  confirmed: true;
  expectedVersion: number;
}>;

/**
 * `POST /api/cases/:caseId/receipts/reverse`. `newVerifiedQuantity` is the
 * audited absolute corrected total (not a delta), always with a reason.
 */
export const reverseVerificationInputSchema = z.object({
  partLineId: uuidSchema,
  newVerifiedQuantity: z.number().int().min(0).max(LIMITS.quantity.max),
  reason: reasonSchema,
  expectedVersion: expectedVersionSchema,
});
export type ReverseVerificationInput = Readonly<{
  partLineId: string;
  /** The audited absolute corrected total, not a delta. */
  newVerifiedQuantity: number;
  reason: string;
  expectedVersion: number;
}>;

// ---------------------------------------------------------------------------
// Parts cases: queries, search, comments, read state
// ---------------------------------------------------------------------------

/**
 * A page size from a query string: an integer 1–100, default 40. Query-string
 * values arrive as strings, so the number is coerced before the range check.
 */
export const pageLimitSchema = z.preprocess(
  (value) => (value === undefined || value === "" ? PAGE_SIZE.default : value),
  z.coerce.number().int().min(1).max(PAGE_SIZE.max),
);

/** An opaque page cursor as the server issued it; its content is validated server-side. */
export const cursorSchema = z.string().min(1).max(1024);

/**
 * A boolean list filter from a query string: absent, `""`, `"false"` or `"0"`
 * mean off; `"true"` or `"1"` mean on. Anything else is a validation error
 * rather than a silently ignored filter.
 */
export const queryFlagSchema = z.preprocess((value) => {
  if (value === undefined || value === "" || value === "false" || value === "0") return false;
  if (value === "true" || value === "1") return true;
  return value;
}, z.boolean());

/** The list's search field: 1–120 characters after trimming; blank means no filter. */
export const listSearchSchema = z.preprocess(
  blankToUndefined,
  z.string().trim().min(LIMITS.search.min).max(LIMITS.search.max).optional(),
);

/** `GET /api/cases` query string. */
export const caseListQuerySchema = z.object({
  view: caseListViewSchema,
  noEta: queryFlagSchema,
  preRo: queryFlagSchema,
  query: listSearchSchema,
  cursor: cursorSchema.optional(),
  limit: pageLimitSchema,
});
export type CaseListQuery = z.output<typeof caseListQuerySchema>;

/** `GET /api/cases/:caseId` query string: which page of the activity timeline to include. */
export const caseDetailQuerySchema = z.object({
  activityCursor: cursorSchema.optional(),
  activityLimit: pageLimitSchema,
});
export type CaseDetailQuery = z.output<typeof caseDetailQuerySchema>;

/** `GET /api/search` query string. */
export const searchQuerySchema = z.object({
  query: z.string().trim().min(LIMITS.search.min).max(LIMITS.search.max),
  cursor: cursorSchema.optional(),
  limit: pageLimitSchema,
});
export type SearchQuery = z.output<typeof searchQuerySchema>;

// ---------------------------------------------------------------------------
// Parts catalog (spec §6; Parts only)
// ---------------------------------------------------------------------------

/** How many characters of a part number must be typed before suggestions are offered. */
export const CATALOG_SUGGEST_MIN_PREFIX = 3;
/** Suggestion list size: default and ceiling. */
export const CATALOG_SUGGEST_LIMIT = { default: 8, max: 20 } as const;

/** A catalog key as the routes carry it: the normalized part number, letters and digits only. */
export const normalizedPartNumberSchema = z.string().regex(/^[A-Z0-9]{1,80}$/, {
  message: "A catalog key is the normalized part number: upper-case letters and digits only.",
});
export const catalogEntryParamsSchema = z.object({ normalizedPartNumber: normalizedPartNumberSchema });

/** `GET /api/catalog` query string. */
export const catalogSearchQuerySchema = z.object({
  q: listSearchSchema,
  includeHidden: queryFlagSchema,
  cursor: cursorSchema.optional(),
  limit: pageLimitSchema,
});
export type CatalogSearchQuery = z.output<typeof catalogSearchQuerySchema>;

/** `GET /api/catalog/lookup?partNumber=` — any spelling; the server normalizes it. */
export const catalogLookupQuerySchema = z.object({
  partNumber: z.string().trim().min(LIMITS.partNumber.min).max(LIMITS.partNumber.max),
});

/** `GET /api/catalog/suggest?prefix=&limit=` — at least three characters as typed. */
export const catalogSuggestQuerySchema = z.object({
  prefix: z.string().trim().min(CATALOG_SUGGEST_MIN_PREFIX).max(LIMITS.partNumber.max),
  limit: z.preprocess(
    (value) => (value === undefined || value === "" ? CATALOG_SUGGEST_LIMIT.default : value),
    z.coerce.number().int().min(1).max(CATALOG_SUGGEST_LIMIT.max),
  ),
});

/**
 * `PATCH /api/catalog/:normalizedPartNumber` — a new description under the
 * entry's `updatedAt` as the client last saw it (409 `STALE_WRITE` when it
 * moved). The catalog page is the only place a description changes.
 */
export const updateCatalogEntryInputSchema = z.object({
  description: partDescriptionSchema,
  expectedUpdatedAt: z.iso.datetime({ offset: true }),
});
export type UpdateCatalogEntryInput = z.infer<typeof updateCatalogEntryInputSchema>;

/** `POST /api/catalog/:normalizedPartNumber/hidden` — hide (`true`) or unhide (`false`). */
export const setCatalogEntryHiddenInputSchema = z.object({
  hidden: z.boolean(),
});
export type SetCatalogEntryHiddenInput = z.infer<typeof setCatalogEntryHiddenInputSchema>;

// ---------------------------------------------------------------------------
// Wholesale (spec 2026-09-17 §3–§5; Parts only)
// ---------------------------------------------------------------------------

export const wholesaleListViewSchema = z.enum(WHOLESALE_LIST_VIEWS);

/** A wholesale customer's phone as typed: trimmed, 1–40 characters. */
export const wholesalePhoneSchema = z.string().trim().min(LIMITS.phone.min).max(LIMITS.phone.max);

/** Free text on the customer record, 1–2000 characters; blank means none. */
export const wholesaleNotesSchema = orderNoteSchema;

/** `POST /api/wholesale/customers`. Only the business name is required; `null` or blank means none. */
export const createWholesaleCustomerInputSchema = z.object({
  name: displayNameSchema,
  contactName: z.preprocess(nullOrBlankToUndefined, displayNameSchema.optional()),
  phone: z.preprocess(nullOrBlankToUndefined, wholesalePhoneSchema.optional()),
  email: z.preprocess(nullOrBlankToUndefined, emailSchema.optional()),
  notes: z.preprocess(nullOrBlankToUndefined, z.string().trim().min(LIMITS.note.min).max(LIMITS.note.max).optional()),
});
export type CreateWholesaleCustomerInput = z.infer<typeof createWholesaleCustomerInputSchema>;

/**
 * `PATCH /api/wholesale/customers/:id`. The optional fields accept `null`
 * (or a blank) to clear; at least one field must be present. The customer's
 * `version` guards the write (409 `STALE_WRITE`).
 */
export const updateWholesaleCustomerInputSchema = z.object({
  patch: z
    .object({
      name: displayNameSchema.optional(),
      contactName: z.preprocess(blankToNull, displayNameSchema.nullable().optional()),
      phone: z.preprocess(blankToNull, wholesalePhoneSchema.nullable().optional()),
      email: z.preprocess(blankToNull, emailSchema.nullable().optional()),
      notes: z.preprocess(
        blankToNull,
        z.string().trim().min(LIMITS.note.min).max(LIMITS.note.max).nullable().optional(),
      ),
    })
    .refine((patch) => Object.values(patch).some((value) => value !== undefined), {
      message: "Change at least one field.",
    }),
  expectedVersion: expectedVersionSchema,
});
export type UpdateWholesaleCustomerInput = z.infer<typeof updateWholesaleCustomerInputSchema>;

/** `POST /api/wholesale/customers/:id/deactivate` and `/reactivate`. */
export const wholesaleCustomerStateInputSchema = z.object({
  expectedVersion: expectedVersionSchema,
});
export type WholesaleCustomerStateInput = z.infer<typeof wholesaleCustomerStateInputSchema>;

/** `GET /api/wholesale/customers` query string: a name search, inactive customers on request. */
export const wholesaleCustomerListQuerySchema = z.object({
  q: listSearchSchema,
  includeInactive: queryFlagSchema,
  cursor: cursorSchema.optional(),
  limit: pageLimitSchema,
});
export type WholesaleCustomerListQuery = z.output<typeof wholesaleCustomerListQuerySchema>;

export const wholesaleCustomerIdParamsSchema = z.object({ id: uuidSchema });

/**
 * A customer reference (the customer's PO) or a Reynolds & Reynolds number
 * as typed, trimmed with internal whitespace collapsed, 1–50 characters.
 * The duplicate key of the reference (`normalizeRo`) is derived by the server.
 */
export const wholesaleReferenceSchema = z
  .string()
  .transform(normalizeName)
  .pipe(z.string().min(LIMITS.wholesaleReference.min).max(LIMITS.wholesaleReference.max));

/**
 * `POST /api/wholesale/orders`. The customer must be active; the reference,
 * the R&R number (attachable later), the master ETA and the note are
 * optional. The part lines are the very shape the service order form
 * sends (`createPartLineInputSchema`): a line without `eta` takes the
 * master ETA.
 */
export const createWholesaleOrderInputSchema = z.object({
  customerId: uuidSchema,
  customerReference: z.preprocess(blankToUndefined, wholesaleReferenceSchema.optional()),
  rrNumber: z.preprocess(blankToUndefined, wholesaleReferenceSchema.optional()),
  masterEta: z.preprocess(blankToUndefined, masterEtaSchema.optional()),
  orderNote: orderNoteSchema,
  duplicateOverrideReason: z.preprocess(blankToUndefined, reasonSchema.optional()),
  partLines: z.array(createPartLineInputSchema).min(1, "Add at least one part line."),
});
export type CreateWholesaleOrderInput = z.infer<typeof createWholesaleOrderInputSchema>;

/**
 * `PATCH /api/wholesale/orders/:orderId`. Every header field is optional;
 * the nullable ones accept `null` (or a blank) to clear. At least one
 * field must be present.
 */
export const updateWholesaleOrderHeaderInputSchema = z.object({
  patch: z
    .object({
      customerId: uuidSchema.optional(),
      customerReference: z.preprocess(blankToNull, wholesaleReferenceSchema.nullable().optional()),
      rrNumber: z.preprocess(blankToNull, wholesaleReferenceSchema.nullable().optional()),
      masterEta: z.preprocess(blankToNull, masterEtaSchema.nullable().optional()),
      orderNote: z.preprocess(
        blankToNull,
        z.string().trim().min(LIMITS.note.min).max(LIMITS.note.max).nullable().optional(),
      ),
    })
    .refine((patch) => Object.values(patch).some((value) => value !== undefined), {
      message: "Change at least one field.",
    }),
  meta: mutationMetaSchema,
  duplicateOverrideReason: z.preprocess(blankToUndefined, reasonSchema.optional()),
});
export type UpdateWholesaleOrderHeaderInput = z.infer<typeof updateWholesaleOrderHeaderInputSchema>;

/** `POST /api/wholesale/orders/:orderId/cancel` — the same shape as a case's. */
export const cancelWholesaleOrderInputSchema = cancelCaseInputSchema;
export type CancelWholesaleOrderInput = z.infer<typeof cancelWholesaleOrderInputSchema>;

/** `GET /api/wholesale/orders` query string; `customerId` narrows any view to one customer. */
export const wholesaleOrderListQuerySchema = z.object({
  view: wholesaleListViewSchema,
  customerId: z.preprocess(blankToUndefined, uuidSchema.optional()),
  cursor: cursorSchema.optional(),
  limit: pageLimitSchema,
});
export type WholesaleOrderListQuery = z.output<typeof wholesaleOrderListQuerySchema>;

/** `GET /api/wholesale/orders/search` query string. */
export const wholesaleSearchQuerySchema = z.object({
  q: z.string().trim().min(LIMITS.search.min).max(LIMITS.search.max),
  cursor: cursorSchema.optional(),
  limit: pageLimitSchema,
});
export type WholesaleSearchQuery = z.output<typeof wholesaleSearchQuerySchema>;

export const wholesaleOrderIdParamsSchema = z.object({ orderId: uuidSchema });
export const wholesaleOrderLineParamsSchema = z.object({ orderId: uuidSchema, lineId: uuidSchema });
/** `GET /api/wholesale/orders/:orderId` query string: which page of the timeline to include. */
export const wholesaleOrderDetailQuerySchema = caseDetailQuerySchema;

// ---------------------------------------------------------------------------
// Reports: the stock order report (owner request 2026-09-17)
// ---------------------------------------------------------------------------

/** The longest range the stock order report answers, in calendar days (a leap year). */
export const STOCK_ORDER_REPORT_MAX_DAYS = 366;

/** A calendar date from a query string, `YYYY-MM-DD`; blank means "not given". */
const localDateQuerySchema = z.preprocess(blankToUndefined, z.iso.date().optional());

/** Which side of the counter a report line came from. */
export const STOCK_ORDER_SIDES = ["service", "wholesale"] as const;
export type StockOrderSide = (typeof STOCK_ORDER_SIDES)[number];

/** The report's Side filter: both sides (default), or one. */
export const stockOrderSideFilterSchema = z.preprocess(
  (value) => (value === undefined || value === "" ? "both" : value),
  z.enum(["both", ...STOCK_ORDER_SIDES]),
);
export type StockOrderSideFilter = z.output<typeof stockOrderSideFilterSchema>;

/**
 * `GET /api/reports/stock-orders` query string. Both dates are inclusive
 * calendar days in the dealership's zone and default to today there; the
 * service refuses `to` before `from` and a span past
 * {@link STOCK_ORDER_REPORT_MAX_DAYS} with the same 422 shape. `side`
 * keeps the report to one side of the counter (wholesale spec §8).
 */
export const stockOrderReportQuerySchema = z.object({
  from: localDateQuerySchema,
  to: localDateQuerySchema,
  side: stockOrderSideFilterSchema,
});
export type StockOrderReportQuery = z.output<typeof stockOrderReportQuerySchema>;

/** `POST /api/cases/:caseId/comments`: 1–2000 characters after trimming. */
export const addCommentInputSchema = z.object({
  body: z.string().trim().min(LIMITS.note.min).max(LIMITS.note.max),
});
export type AddCommentInput = z.infer<typeof addCommentInputSchema>;

/**
 * `POST /api/cases/:caseId/read`: the exact `readThroughActivityEventId` the
 * detail response carried. The server checks it belongs to the case.
 */
export const markReadInputSchema = z.object({
  throughActivityEventId: uuidSchema,
});
export type MarkReadInput = z.infer<typeof markReadInputSchema>;

// ---------------------------------------------------------------------------
// Authentication
// ---------------------------------------------------------------------------

/**
 * Login accepts any non-empty password up to the maximum so that a wrong
 * password of the wrong shape still counts as a failed attempt; the 12-character
 * policy applies when a password is *set*, not when it is tried.
 */
export const loginInputSchema = z.object({
  email: z.string().trim().min(1).max(LIMITS.email.max),
  password: z.string().min(1).max(LIMITS.password.max),
});
export type LoginInput = z.infer<typeof loginInputSchema>;

export const changePasswordInputSchema = z
  .object({
    currentPassword: z.string().min(1).max(LIMITS.password.max),
    newPassword: passwordSchema,
  })
  .refine((input) => input.currentPassword !== input.newPassword, {
    path: ["newPassword"],
    message: "The new password must be different from the current password.",
  });
export type ChangePasswordInput = z.infer<typeof changePasswordInputSchema>;

// ---------------------------------------------------------------------------
// Account administration
// ---------------------------------------------------------------------------

/**
 * `POST /api/accounts` — name, email and role; `temporaryPassword` is the
 * one the administrator typed, or absent to have one generated. The client
 * omits the key for a blank field: an empty string is a bad value, not "none".
 */
export const createAccountInputSchema = z.object({
  displayName: displayNameSchema,
  email: emailSchema,
  role: roleSchema,
  temporaryPassword: temporaryPasswordSchema.optional(),
});
export type CreateAccountInput = z.infer<typeof createAccountInputSchema>;

/**
 * `POST /api/accounts/:id/reset-password` — an optional body: the chosen
 * temporary password, or nothing at all (no body, `{}`) to generate one.
 */
export const resetPasswordInputSchema = z.object({
  temporaryPassword: temporaryPasswordSchema.optional(),
});
export type ResetPasswordInput = z.infer<typeof resetPasswordInputSchema>;

export const assignRoleInputSchema = z.object({
  role: roleSchema,
  expectedVersion: expectedVersionSchema,
});
export type AssignRoleInput = z.infer<typeof assignRoleInputSchema>;

export const deactivateAccountInputSchema = z.object({
  expectedVersion: expectedVersionSchema,
});
export type DeactivateAccountInput = z.infer<typeof deactivateAccountInputSchema>;

/** `PATCH /api/accounts/:id/name` — a new display name, under the account's version. */
export const renameAccountInputSchema = z.object({
  displayName: displayNameSchema,
  expectedVersion: expectedVersionSchema,
});
export type RenameAccountInput = z.infer<typeof renameAccountInputSchema>;

/**
 * `POST /api/accounts/:id/administration` — grant (`isAdmin: true`) or
 * revoke (`isAdmin: false`) the administration capability, under the
 * account's version. The server refuses to remove the last active
 * administrator (409 `LAST_ADMINISTRATOR`).
 */
export const setAdministrationInputSchema = z.object({
  isAdmin: z.boolean(),
  expectedVersion: expectedVersionSchema,
});
export type SetAdministrationInput = z.infer<typeof setAdministrationInputSchema>;

export const accountIdParamsSchema = z.object({ id: uuidSchema });

/**
 * What the account-administration history records (`GET
 * /api/accounts/history`): one row per account mutation, written inside the
 * mutation's own transaction. The bootstrap paths (startup and
 * `bootstrap-admin`) write the same types with no actor. The two
 * `invitation_*` types are the outcome of the temporary-password email a
 * creation or reset sends after it commits, written in their own short
 * transaction once the send has been attempted; their metadata names the
 * address and, for a failure, the mailer's reason — never the password.
 */
export const ACCOUNT_EVENT_TYPES = [
  "account_created",
  "role_changed",
  "name_changed",
  "account_deactivated",
  "account_reactivated",
  "password_reset",
  "administration_granted",
  "administration_revoked",
  "invitation_sent",
  "invitation_failed",
] as const;
export type AccountEventType = (typeof ACCOUNT_EVENT_TYPES)[number];

// ---------------------------------------------------------------------------
// Public DTO types (canonical interfaces)
// ---------------------------------------------------------------------------

export type ApiErrorBody = Readonly<{
  error: {
    code: string;
    message: string;
    fieldErrors?: Readonly<Record<string, readonly string[]>>;
    details?: unknown;
  };
}>;

/**
 * The signed-in employee as login, password change and the session probe
 * report them. `GET /api/auth/session` answers `{ user: SessionUser }` for a
 * live session (a temporary-password session included, with
 * `mustChangePassword` set) and 200 `{ user: null }` when nobody is signed in
 * — the probe is a question, not a guarded resource, so an anonymous visit to
 * the sign-in page produces no failed request. The other public routes are
 * sign-in (answers on its credentials), sign-out (204, idempotent) and the
 * health probes outside `/api`; every guarded `/api` read and mutation
 * answers an anonymous caller with 401 `UNAUTHENTICATED`.
 */
export type SessionUser = Readonly<{
  id: string;
  displayName: string;
  email: string;
  role: Role;
  isAdmin: boolean;
  mustChangePassword: boolean;
}>;

/**
 * One account for the administration list. `temporaryPasswordExpiresAt` is
 * the ISO instant a still-unreplaced temporary password stops working, and
 * null when there is no such deadline — the password was chosen by the
 * person (or by the operator, for the bootstrap administrator).
 */
export type AccountView = Readonly<{
  id: string;
  displayName: string;
  email: string;
  role: Role;
  isAdmin: boolean;
  active: boolean;
  temporaryPasswordExpiresAt: string | null;
  version: number;
}>;

/**
 * What became of the temporary-password email a creation or reset sends
 * (`POST /api/accounts`, `POST /api/accounts/:id/reset-password`). A failed
 * send fails neither the request nor the account: the administrator still
 * holds the password and hands it over another way; `reason` is the mailer's
 * one-line explanation. `expiresAt` is the ISO instant the password stops
 * working, sent or not.
 */
export type InvitationOutcome = Readonly<{
  state: "sent" | "failed";
  to: string;
  expiresAt: string;
  reason?: string;
}>;

/**
 * One account-administration history entry. `actorId`/`actorName` are null
 * when the deployment itself made the change (the startup bootstrap or
 * `bootstrap-admin`), never for a person. A `password_reset` carries no
 * changes: neither the password nor its hash is ever recorded.
 */
export type AccountEventView = Readonly<{
  id: string;
  accountId: string;
  accountName: string;
  actorId: string | null;
  actorName: string | null;
  eventType: AccountEventType;
  changes: readonly {
    field: string;
    previous: JsonValue;
    next: JsonValue;
  }[];
  createdAt: string;
}>;

export type CursorPage<T> = Readonly<{
  items: readonly T[];
  nextCursor: string | null;
}>;

export type JsonValue =
  | null
  | boolean
  | number
  | string
  | readonly JsonValue[]
  | { readonly [key: string]: JsonValue };

export type EtaInput =
  | Readonly<{ kind: "date"; date: string }>
  | Readonly<{ kind: "no_eta" }>;

export type MutationMeta = Readonly<{
  expectedVersion: number;
  correctionReason?: string;
}>;

export type CreatePartLineInput = Readonly<{
  partNumber: string;
  description: string;
  quantityOrdered: number;
  /** Absent means "take the order's master ETA" (spec §6.5); an error when there is none. */
  eta?: EtaInput;
}>;

export type CreateCaseInput = Readonly<{
  /** Optional since 2026-09-17; absent or blank means the order has no VIN yet. */
  vin?: string;
  customerName: string;
  vehicleYear: number;
  vehicleMake: string;
  vehicleModel: string;
  advisorId: string;
  /** Optional since 2026-09-17; absent or blank means no technician is assigned. */
  technicianId?: string;
  roNumber?: string;
  appointmentDate?: string;
  vendorOrderReference?: string;
  orderNote?: string;
  /** "ETA for all parts", `YYYY-MM-DD`: fills every line sent without an ETA. */
  masterEta?: string;
  /** The customer's car is at the dealership now. Absent means false. */
  carInShop?: boolean;
  duplicateOverrideReason?: string;
  partLines: readonly CreatePartLineInput[];
}>;

export type ArrivalTransition =
  | Readonly<{ kind: "none" }>
  | Readonly<{
      kind: "arrived" | "arrival_corrected";
      transitionId: string;
      emailEventId: string;
    }>;

export type CaseMutationResult = Readonly<{
  caseId: string;
  caseNumber: string;
  version: number;
  status: CaseStatus;
  arrivedAt: Date | null;
  transition: ArrivalTransition;
}>;

/**
 * A wholesale order's arrival transition: the same two transitions a case
 * has, without an email — nothing is sent when a wholesale order arrives
 * (wholesale spec §5: the customer email is manual).
 */
export type WholesaleTransition =
  | Readonly<{ kind: "none" }>
  | Readonly<{ kind: "arrived" | "arrival_corrected"; transitionId: string }>;

/**
 * What every wholesale mutation answers (`POST /api/wholesale/orders`, the
 * header patch, cancel, the line and receipt routes): the order's id and WS
 * number, its new version, the server-derived status and `arrivedAt`, and
 * the transition. `arrivedAt` crosses the wire as an ISO string.
 */
export type WholesaleMutationResult = Readonly<{
  orderId: string;
  number: string;
  version: number;
  status: CaseStatus;
  arrivedAt: Date | null;
  transition: WholesaleTransition;
}>;

export type RecipientWarning = Readonly<{
  userId: string;
  displayName: string;
  role: Role;
  reason: "inactive" | "missing_email";
}>;

export type PartLineView = Readonly<{
  id: string;
  partNumber: string;
  description: string;
  quantityOrdered: number;
  quantityVerified: number;
  eta: EtaInput;
  exception: PartException | null;
  exceptionReason: string | null;
}>;

export type CaseListItem = Readonly<{
  id: string;
  caseNumber: string;
  roNumber: string | null;
  customerName: string;
  vehicleLabel: string;
  /** `null` while the order has no VIN (the interface shows "—"). */
  vinEnding: string | null;
  advisorName: string;
  /** `null` while no technician is assigned (the interface shows "Unassigned"). */
  technicianName: string | null;
  status: CaseStatus;
  needsAttention: boolean;
  /** Badged in the list so an order for a car that is here stands out on screen. */
  carInShop: boolean;
  quantityOrdered: number;
  quantityVerified: number;
  activeUntil: string | null;
  unread: boolean;
  updatedAt: string;
}>;

export type CommentView = Readonly<{
  id: string;
  authorId: string;
  authorName: string;
  body: string;
  createdAt: string;
  activityEventId: string;
}>;

export type ActivityEventView = Readonly<{
  id: string;
  actorName: string | null;
  eventType: string;
  changes: readonly {
    field: string;
    previous: JsonValue;
    next: JsonValue;
  }[];
  /**
   * The event's business context as the server recorded it — the line
   * (`lineId`, `partNumber`), the quantity received, a reason, an email
   * event — never a secret. The timeline's sentences read it (spec §7).
   */
  metadata: Readonly<Record<string, JsonValue>>;
  /**
   * Display names, by user id, for every person this event's changes refer
   * to (`advisorId` / `technicianId` previous and next), resolved when the
   * event is read — so the timeline names someone who has since left the
   * order. Empty when the event names nobody.
   */
  names: Readonly<Record<string, string>>;
  createdAt: string;
}>;

export type CaseDetail = Readonly<{
  case: CaseListItem & {
    vin: string | null;
    vehicleYear: number;
    vehicleMake: string;
    vehicleModel: string;
    /** The assigned people by id, so an edit can change the assignment by id rather than by name. */
    advisorId: string;
    technicianId: string | null;
    appointmentDate: string | null;
    vendorOrderReference: string | null;
    orderNote: string | null;
    /** "ETA for all parts", `YYYY-MM-DD`, or null when none was given. */
    masterEta: string | null;
    /** The customer's car is at the dealership now. */
    carInShop: boolean;
    version: number;
  };
  partLines: readonly PartLineView[];
  comments: readonly CommentView[];
  activity: CursorPage<ActivityEventView>;
  readThroughActivityEventId: string | null;
  permissions: {
    canEditOrder: boolean;
    canVerifyArrival: boolean;
    canPrintReceipt: boolean;
    canRetryEmail: boolean;
    canComment: boolean;
  };
}>;

export type SearchHit = Readonly<{
  case: CaseListItem;
  lifecycle: "active" | "arrived_48h" | "historical" | "cancelled";
  matchedField: string;
}>;

/**
 * `GET /api/cases/counts` — the Parts center-list counters (spec §10):
 * non-cancelled cases with an unresolved exception, Partially arrived cases,
 * and Arrived cases still inside their 48-hour window, all at the server's
 * clock.
 */
export type CaseCounts = Readonly<{
  needsAttention: number;
  partiallyArrived: number;
  arrived: number;
}>;

/**
 * One recipient of a logical email event, frozen when the transition
 * occurred (`email_deliveries`). Retries never add or change recipients.
 */
export type EmailDeliveryView = Readonly<{
  id: string;
  recipientName: string;
  recipientEmail: string;
  /** `null` for a wholesale customer, who holds no account (the interface says "Customer"). */
  recipientRole: Role | null;
  state: EmailState;
  attempts: number;
  lastError: string | null;
  sentAt: string | null;
}>;

/**
 * `GET /api/email-events/:emailEventId` (Parts only). One logical email per
 * arrival or correction transition (`email_events`), or per manual
 * wholesale customer email; the server renders it and the Parts inspector
 * shows a failure banner with Retry from it. Exactly one of `caseId` /
 * `wholesaleOrderId` names the order. Subject and scheduling are public;
 * the rendered text/HTML bodies and the template payload deliberately are
 * not.
 */
export type EmailEventView = Readonly<{
  id: string;
  caseId: string | null;
  wholesaleOrderId: string | null;
  transitionId: string;
  kind: EmailEventKind;
  subject: string;
  state: EmailState;
  attempts: number;
  nextAttemptAt: string | null;
  lastError: string | null;
  createdAt: string;
  recipientWarnings: readonly RecipientWarning[];
  deliveries: readonly EmailDeliveryView[];
}>;

/**
 * `GET /api/assignees` (Parts only) — the *active* service advisors and
 * technicians a case can be assigned to (spec §5, §14 "People"), by display
 * name. Nothing but id and name: the administrator-only account list stays
 * the only place email addresses and account flags are read.
 */
export type AssigneeOption = Readonly<{
  id: string;
  displayName: string;
}>;

export type AssigneeDirectory = Readonly<{
  advisors: readonly AssigneeOption[];
  technicians: readonly AssigneeOption[];
}>;

export type ReceiptViewModel = Readonly<{
  caseNumber: string;
  createdAt: string;
  vehicleLabel: string;
  roNumber: string | null;
  customerName: string;
  vinEnding: string | null;
  advisorName: string;
  technicianName: string | null;
  lines: readonly {
    partNumber: string;
    description: string;
    quantityOrdered: number;
    etaLabel: string;
  }[];
  orderNote: string | null;
  /** Prints as a large "CAR IN SHOP" band at the top of the receipt. */
  carInShop: boolean;
  creatorName: string;
  printedAt: string;
}>;

/**
 * Where a part line stands, as the case detail names it: its unresolved
 * exception when it has one, otherwise the same words the case status uses
 * for the line's own quantities — nothing verified, some, or all of it.
 */
export type StockOrderLineStatus = PartException | "ordered" | "partially_arrived" | "arrived";

/**
 * One part line ordered inside the report's range, with the order it
 * belongs to — a service case or, since the wholesale side (wholesale spec
 * §8), a wholesale order. `side` says which; `caseId` / `caseNumber` name
 * the owning order on either side (a `WS-` number for wholesale). The
 * vehicle, VIN, advisor and technician exist only on the service side and
 * are null on a wholesale line; the customer reference and R&R number only
 * on the wholesale side.
 */
export type StockOrderReportLine = Readonly<{
  lineId: string;
  side: StockOrderSide;
  /** The instant the line was added to its order (ISO-8601 UTC). */
  orderedAt: string;
  partNumber: string;
  description: string;
  quantityOrdered: number;
  quantityVerified: number;
  status: StockOrderLineStatus;
  /** The owning order's id: a case on the service side, a wholesale order on the other. */
  caseId: string;
  /** The owning order's number: `PC-…` or `WS-…`. */
  caseNumber: string;
  roNumber: string | null;
  /** Service only; false for a wholesale line. */
  isPreRo: boolean;
  /** The service customer, or the wholesale customer's business name. */
  customerName: string;
  /** `null` on a wholesale line. */
  vehicle: Readonly<{ year: number; make: string; model: string }> | null;
  /** `null` once an order can be opened without a VIN (the interface shows "—"), and on a wholesale line. */
  vinEnding: string | null;
  /** `null` on a wholesale line. */
  advisorName: string | null;
  /** `null` while no technician is assigned (the interface shows "Unassigned"), and on a wholesale line. */
  technicianName: string | null;
  eta: EtaInput;
  vendorOrderReference: string | null;
  /** Wholesale only: the customer's reference (PO). */
  customerReference: string | null;
  /** Wholesale only: the Reynolds & Reynolds number. */
  rrNumber: string | null;
  /** The whole order was cancelled, or this line was; it was still ordered that day. */
  cancelled: boolean;
}>;

/**
 * One part number across every line that ordered it in the range. The
 * quantities of cancelled lines (and of lines on cancelled orders) are kept
 * apart in `quantityCancelled` and never in `quantityOrdered` or
 * `quantityVerified`: this table is the stock order, and a part nobody wants
 * any more must not be placed from it.
 */
export type StockOrderReportPart = Readonly<{
  partNumber: string;
  /** The description as it was spelled on the most recently ordered line. */
  description: string;
  /** Summed over the live lines only. */
  quantityOrdered: number;
  /** Summed over the live lines only. */
  quantityVerified: number;
  /** Summed over the cancelled lines: ordered in the range, not wanted. */
  quantityCancelled: number;
  /** How many distinct orders the part appears on, cancelled or not. */
  orders: number;
}>;

/** The range's totals; the quantities split live from cancelled the same way the parts do. */
export type StockOrderReportTotals = Readonly<{
  /** Every line in the range, cancelled ones included. */
  lines: number;
  quantityOrdered: number;
  quantityVerified: number;
  quantityCancelled: number;
  distinctParts: number;
  /** Every order with a line in the range, cancelled ones included. */
  orders: number;
}>;

export type StockOrderReportRange = Readonly<{
  /** Inclusive calendar dates in `timeZone`, `YYYY-MM-DD`. */
  from: string;
  to: string;
  timeZone: string;
  /** The half-open instant window the dates cover, ISO-8601 UTC. */
  startsAt: string;
  endsAt: string;
  /** Which side(s) of the counter the report covers. */
  side: StockOrderSideFilter;
}>;

/** `GET /api/reports/stock-orders`: every part line ordered in the range. */
export type StockOrderReport = Readonly<{
  range: StockOrderReportRange;
  totals: StockOrderReportTotals;
  /** Sorted by part number. */
  byPart: readonly StockOrderReportPart[];
  /** Sorted by the instant ordered, then part number. */
  lines: readonly StockOrderReportLine[];
}>;

/**
 * One parts-catalog entry (spec §3.5): the number as first typed, its
 * normalized key, the description the counter auto-fills from, and the
 * bookkeeping the catalog page shows. `updatedAt` is the optimistic key of
 * a description edit; `updatedByName` is null when the back-fill wrote the
 * row and nobody has edited it since.
 */
export type CatalogEntryView = Readonly<{
  partNumber: string;
  normalizedPartNumber: string;
  description: string;
  hidden: boolean;
  timesUsed: number;
  firstSeenAt: string;
  updatedAt: string;
  updatedByName: string | null;
}>;

/** `GET /api/catalog/lookup` — the entry, or `null` when unknown or hidden. */
export type CatalogLookupResult = Readonly<{ entry: CatalogEntryView | null }>;

/** `GET /api/catalog/suggest` — matches by prefix, most used first, hidden excluded. */
export type CatalogSuggestions = Readonly<{ items: readonly CatalogEntryView[] }>;

export type CaseHeaderPatch = Partial<{
  /** `null` clears the VIN. */
  vin: string | null;
  customerName: string;
  vehicleYear: number;
  vehicleMake: string;
  vehicleModel: string;
  advisorId: string;
  /** `null` unassigns the technician. */
  technicianId: string | null;
  roNumber: string | null;
  appointmentDate: string | null;
  vendorOrderReference: string | null;
  orderNote: string | null;
  /** `null` clears the master ETA; setting one changes no existing line (every stored line holds a choice). */
  masterEta: string | null;
  carInShop: boolean;
}>;

export type AddLineInput = Readonly<
  CreatePartLineInput & {
    meta: MutationMeta;
  }
>;

export type UpdateLineInput = Readonly<{
  patch: Partial<CreatePartLineInput>;
  meta: MutationMeta;
}>;

export type DuplicateCandidate = Readonly<{
  caseId: string;
  caseNumber: string;
  reason: "same_ro" | "same_vin_and_part";
  partNumber?: string;
}>;

// ---------------------------------------------------------------------------
// Wholesale views (spec 2026-09-17 §4; Parts only)
// ---------------------------------------------------------------------------

/**
 * One wholesale customer as the directory and the order pages show it. A
 * deactivated customer (`active: false`) stays on its old orders and cannot
 * be picked for new ones. `version` guards edits.
 */
export type WholesaleCustomerView = Readonly<{
  id: string;
  name: string;
  contactName: string | null;
  phone: string | null;
  email: string | null;
  notes: string | null;
  active: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
}>;

/**
 * One wholesale order as the lists show it. `activeUntil` is the instant
 * an Arrived order leaves the Arrived list (`arrivedAt` + 36 hours), null
 * otherwise; `needsAttention` is the same rule as a case's (an unresolved
 * exception on a non-cancelled order); `unread` is the caller's own.
 */
export type WholesaleOrderSummary = Readonly<{
  id: string;
  number: string;
  customerId: string;
  customerName: string;
  customerReference: string | null;
  rrNumber: string | null;
  masterEta: string | null;
  status: CaseStatus;
  needsAttention: boolean;
  quantityOrdered: number;
  quantityVerified: number;
  arrivedAt: string | null;
  activeUntil: string | null;
  unread: boolean;
  createdAt: string;
  updatedAt: string;
}>;

/** `GET /api/wholesale/orders/search` — one hit: the order, where it stands, and the first field that matched. */
export type WholesaleSearchHit = Readonly<{
  order: WholesaleOrderSummary;
  lifecycle: "active" | "arrived" | "historical" | "cancelled";
  matchedField: string;
}>;

/**
 * One manual customer email (`wholesale_ready`) on a wholesale order: who
 * asked for it, when, to which address, and how the outbox got on. Retry
 * goes through `POST /api/email-events/:id/retry` like an arrival email.
 */
export type WholesaleEmailView = Readonly<{
  id: string;
  orderId: string;
  to: string;
  subject: string;
  state: EmailState;
  attempts: number;
  nextAttemptAt: string | null;
  lastError: string | null;
  sentAt: string | null;
  createdAt: string;
  requestedByName: string | null;
}>;

/** `GET /api/wholesale/orders/:orderId/email/preview` — what Send would enqueue, before it does. */
export type WholesaleEmailPreview = Readonly<{
  to: string | null;
  from: string;
  subject: string;
  text: string;
}>;

/**
 * Why the Email customer action is unavailable, for the button's hint:
 * `no_email` — "No email on file for <customer>"; `not_arrived` — nothing
 * has been verified in yet; `cancelled` — the order is cancelled.
 */
export type WholesaleEmailDisabledReason = "no_email" | "not_arrived" | "cancelled";

export type WholesaleOrderDetail = Readonly<{
  order: WholesaleOrderSummary & {
    customer: WholesaleCustomerView;
    orderNote: string | null;
    cancelledAt: string | null;
    cancellationReason: string | null;
    createdByName: string;
    version: number;
  };
  partLines: readonly PartLineView[];
  comments: readonly CommentView[];
  activity: CursorPage<ActivityEventView>;
  readThroughActivityEventId: string | null;
  /** Every customer email sent from this order, newest first. */
  emails: readonly WholesaleEmailView[];
  capabilities: {
    canEditOrder: boolean;
    canVerifyArrival: boolean;
    canCancel: boolean;
    canEmailCustomer: boolean;
    emailCustomerDisabledReason: WholesaleEmailDisabledReason | null;
    canRetryEmail: boolean;
    canComment: boolean;
  };
}>;

/**
 * The printable wholesale order ticket — the wholesale twin of
 * `ReceiptViewModel`. A projection, not a DTO: the service selects only the
 * columns below, so nothing the ticket excludes (comments, activity, email
 * state, verification, cancellation reason, capabilities) can reach paper
 * through a later change to the page.
 *
 * Wholesale has a customer account rather than a vehicle and an RO, so the
 * header carries the account name, the customer's own reference and the RR
 * number in place of the vehicle block; everything else mirrors the service
 * receipt line for line.
 */
export type WholesaleTicketViewModel = Readonly<{
  orderNumber: string;
  createdAt: string;
  customerName: string;
  customerReference: string | null;
  rrNumber: string | null;
  masterEtaLabel: string | null;
  lines: readonly {
    partNumber: string;
    description: string;
    quantityOrdered: number;
    etaLabel: string;
  }[];
  orderNote: string | null;
  creatorName: string;
  printedAt: string;
}>;

export type WholesaleOrderHeaderPatch = Partial<{
  customerId: string;
  customerReference: string | null;
  rrNumber: string | null;
  masterEta: string | null;
  orderNote: string | null;
}>;

/** A wholesale order another order duplicates: same customer, same reference, inside 30 days. */
export type WholesaleDuplicateCandidate = Readonly<{
  orderId: string;
  number: string;
  createdAt: string;
}>;
